import axios from 'axios';
import { API_BASE_URL, endpoints } from 'core/api/endpoints';
import { notifySessionExpired, resetSessionExpiredNotification } from 'core/api/sessionEvents';
import { asObject, unwrapEnvelope } from 'core/api/unwrap';
import { ApiError } from 'core/errors/ApiError';
import { isConfirmedAuthFailure, isTemporaryAuthFailure, isTokenPersistenceError } from 'auth/sessionClassification';
import { applyTokenPayload, getAccessToken, getRefreshToken, setTokens, clearTokens } from 'auth/tokenStore';
import { getLocale } from 'core/i18n/localeStore';

const PUBLIC_AUTH_PATHS = [
  endpoints.otpRequest,
  endpoints.otpVerify,
  endpoints.profileOptions,
  endpoints.refresh,
];

const NON_RETRY_STATUSES = new Set([409, 422, 429]);

let refreshPromise = null;

function isPublicAuthRequest(config = {}) {
  if (config.skipAuth) {
    return true;
  }
  const url = String(config.url || '');
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
}

function shouldSkipRefresh(config = {}) {
  return Boolean(config.skipRefresh) || isPublicAuthRequest(config);
}

function readHeader(headers, name) {
  if (!headers) {
    return undefined;
  }
  if (typeof headers.get === 'function') {
    return headers.get(name) || headers.get(name.toLowerCase()) || undefined;
  }
  return headers[name] || headers[name.toLowerCase()] || headers[name.toUpperCase()];
}

function authorizationWasAttached(config = {}) {
  const headers = config.headers || {};
  return Boolean(readHeader(headers, 'Authorization'));
}

function looksUnsafeServerText(value) {
  const text = String(value || '');
  if (!text) {
    return true;
  }
  if (text.length > 280) {
    return true;
  }
  return /stack trace|sqlstate|exception|Illuminate\\|vendor\/laravel|on line \d+|<\s*html|<\s*body/i.test(text);
}

function contextualErrorData(payload) {
  const data = { ...asObject(payload.data) };
  ['remaining_attempts', 'next_step', 'is_new_user', 'retry_after_seconds', 'retry_after', 'resend_after_seconds'].forEach((key) => {
    if (data[key] == null && payload[key] != null) {
      data[key] = payload[key];
    }
  });
  return data;
}

function readRetryAfter(headers, payload) {
  const header = readHeader(headers, 'Retry-After') || readHeader(headers, 'retry-after');
  if (header && /^\d+$/.test(String(header).trim())) {
    return Number(header);
  }
  const data = asObject(payload?.data);
  const nested = Number(data.retry_after_seconds || data.retry_after || data.resend_after_seconds);
  if (Number.isFinite(nested) && nested > 0) {
    return nested;
  }
  const top = Number(payload?.retry_after_seconds || payload?.retry_after);
  return Number.isFinite(top) && top > 0 ? top : null;
}

export function toApiError(error) {
  if (error instanceof ApiError) {
    return error;
  }

  if (!error.response) {
    return new ApiError({
      status: 0,
      message: error.message || 'Network error',
      isNetworkError: true,
    });
  }

  const { status, data, headers } = error.response;
  const payload = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  const errors = asObject(payload.errors);
  const contextual = contextualErrorData(payload);
  const rawMessage = payload.message || (typeof data === 'string' ? data : error.message);
  const malformed = typeof data !== 'object' || data == null || Array.isArray(data);
  const serverFailure = status >= 500 || malformed;
  const unsafe = looksUnsafeServerText(rawMessage);
  const message = serverFailure || unsafe ? 'Request failed' : (rawMessage || 'Request failed');

  return new ApiError({
    status,
    message,
    errors,
    data: contextual,
    payload,
    retryAfterSeconds: readRetryAfter(headers, payload),
    isMalformed: malformed,
  });
}

function applySessionTokens(data) {
  return applyTokenPayload(data);
}

function persistRotatedPair(data) {
  if (!data?.access_token || !data?.refresh_token) {
    throw new ApiError({
      status: 500,
      message: 'Request failed',
      isMalformed: true,
    });
  }
  setTokens({
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  });
  return data;
}

function expireConfirmedSession(clearTokensFn = clearTokens) {
  try {
    clearTokensFn();
  } catch {
    // Session expiration still notifies listeners if storage teardown fails.
  }
  notifySessionExpired();
}

export function createRefreshController(overrides = {}) {
  return {
    async refresh(options = {}) {
      if (refreshPromise) {
        return refreshPromise;
      }

      refreshPromise = (async () => {
        const refreshToken = options.refreshToken || (overrides.getRefreshToken || getRefreshToken)();
        if (!refreshToken) {
          throw new ApiError({ status: 401, message: 'Missing refresh token' });
        }

        const client = overrides.refreshClient || axios.create({
          baseURL: overrides.baseURL || API_BASE_URL,
          timeout: 30000,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Language': (overrides.getLocale || getLocale)(),
          },
          ...(overrides.adapter ? { adapter: overrides.adapter } : {}),
        });

        const response = await client.post(endpoints.refresh, { refresh_token: refreshToken });
        const data = unwrapEnvelope(response.data);
        persistRotatedPair(data);
        return data;
      })().finally(() => {
        refreshPromise = null;
      });

      return refreshPromise;
    },
    reset() {
      refreshPromise = null;
    },
  };
}

const refreshController = createRefreshController();

export function refreshSession(options = {}) {
  return refreshController.refresh(options);
}

export function createApiClient(overrides = {}) {
  const controller = overrides.refreshController || refreshController;
  const client = axios.create({
    baseURL: overrides.baseURL || API_BASE_URL,
    timeout: overrides.timeout || 30000,
    withCredentials: false,
    ...(overrides.adapter ? { adapter: overrides.adapter } : {}),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = (overrides.getAccessToken || getAccessToken)();
    const locale = (overrides.getLocale || getLocale)();
    config.headers = config.headers || {};
    if (token && !isPublicAuthRequest(config)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (locale) {
      config.headers['X-Language'] = locale;
      config.headers['Accept-Language'] = locale;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      try {
        const data = unwrapEnvelope(response.data);
        applySessionTokens(data);
        response.data = data;
        return response;
      } catch (error) {
        if (isTokenPersistenceError(error)) {
          return Promise.reject(error);
        }
        const apiError = error instanceof ApiError
          ? new ApiError({
            status: error.status || response.status || 0,
            message: error.isMalformed ? 'Request failed' : error.message,
            errors: error.errors,
            data: error.data,
            payload: error.payload,
            retryAfterSeconds: error.retryAfterSeconds,
            isMalformed: error.isMalformed,
          })
          : toApiError(error);
        return Promise.reject(apiError);
      }
    },
    async (error) => {
      if (isTokenPersistenceError(error)) {
        return Promise.reject(error);
      }
      const apiError = toApiError(error);
      const config = error.config || {};

      if (NON_RETRY_STATUSES.has(apiError.status) || apiError.isValidation()) {
        return Promise.reject(apiError);
      }

      const alreadyRetried = Boolean(config.__retriedAfterRefresh || readHeader(config.headers, 'X-Retry-After-Refresh'));
      if (
        apiError.status === 401
        && authorizationWasAttached(config)
        && !shouldSkipRefresh(config)
        && !alreadyRetried
      ) {
        try {
          await controller.refresh();
          const retryHeaders = {
            ...(config.headers || {}),
          };
          retryHeaders.Authorization = `Bearer ${(overrides.getAccessToken || getAccessToken)()}`;
          retryHeaders['X-Retry-After-Refresh'] = '1';
          return client.request({
            ...config,
            __retriedAfterRefresh: true,
            headers: retryHeaders,
          });
        } catch (refreshError) {
          if (isConfirmedAuthFailure(refreshError) && !isTemporaryAuthFailure(refreshError)) {
            expireConfirmedSession(overrides.clearTokens || clearTokens);
          }
          return Promise.reject(apiError);
        }
      }

      if (
        apiError.status === 401
        && authorizationWasAttached(config)
        && !shouldSkipRefresh(config)
        && alreadyRetried
      ) {
        expireConfirmedSession(overrides.clearTokens || clearTokens);
      }

      return Promise.reject(apiError);
    },
  );

  return client;
}

export const apiClient = createApiClient();

export async function apiGet(url, config) {
  const response = await apiClient.get(url, config);
  return response.data;
}

export async function apiPost(url, body, config) {
  const response = await apiClient.post(url, body, config);
  return response.data;
}

export async function apiPatch(url, body, config) {
  const response = await apiClient.patch(url, body, config);
  return response.data;
}

export async function apiPut(url, body, config) {
  const response = await apiClient.put(url, body, config);
  return response.data;
}

export async function apiDelete(url, config) {
  const response = await apiClient.delete(url, config);
  return response.data;
}

export function resetRefreshLock() {
  refreshController.reset();
  resetSessionExpiredNotification();
}
